import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { NotificationPreferences, NotificationType } from '../../types/notifications';
import { Bell, Settings } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import toast from 'react-hot-toast';
import { useNotifications } from '../../contexts/NotificationContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

export default function NotificationSettingsPage() {
  useDocumentTitle('Notification Settings');
  
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    email: true,
    push: true,
    desktop: true,
    categories: {
      task: { enabled: true, priority: ['medium', 'high'] },
      message: { enabled: true, priority: ['high'] },
      calendar: { enabled: true, priority: ['medium', 'high'] },
      document: { enabled: true, priority: ['high'] },
      attendance: { enabled: true, priority: ['high'] },
      system: { enabled: true, priority: ['high'] },
      lms: { enabled: true, priority: ['medium', 'high'] }
    },
    quietHours: {
      enabled: false,
      start: '22:00',
      end: '07:00'
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { notificationsEnabled, requestPermission } = useNotifications();

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists() && docSnap.data().notificationPreferences) {
          setPreferences(docSnap.data().notificationPreferences);
        }
      } catch (error) {
        console.error('Error loading notification preferences:', error);
        toast.error('Failed to load preferences');
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const user = auth.currentUser;
      if (!user) return;

      await setDoc(doc(db, 'users', user.uid), {
        notificationPreferences: preferences
      }, { merge: true });

      toast.success('Preferences saved successfully');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleEnableDesktopNotifications = async () => {
    const enabled = await requestPermission();
    if (enabled) {
      setPreferences(prev => ({ ...prev, desktop: true }));
      toast.success('Desktop notifications enabled');
    } else {
      toast.error('Failed to enable desktop notifications');
    }
  };

  const categoryLabels: Record<NotificationType, string> = {
    task: 'Tasks and Assignments',
    message: 'Messages and Chats',
    calendar: 'Calendar Events',
    document: 'Document Updates',
    attendance: 'Attendance Alerts',
    system: 'System Updates',
    lms: 'LMS Integration'
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-text-secondary">Loading preferences...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <Bell className="w-8 h-8 text-primary-500" />
            <div>
              <h1 className="text-2xl font-semibold text-text-primary">Notification Settings</h1>
              <p className="text-text-secondary">Customize how you receive notifications</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Delivery Methods */}
            <div className="bg-surface-light rounded-lg p-6">
              <h2 className="text-lg font-semibold text-text-primary mb-4">Delivery Methods</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-text-primary">Email Notifications</h3>
                    <p className="text-sm text-text-secondary">Receive updates via email</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.email}
                      onChange={(e) => setPreferences({ ...preferences, email: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-border-light peer-focus:outline-none rounded-full peer 
                                peer-checked:after:translate-x-full peer-checked:after:border-white 
                                after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                                after:bg-white after:border-gray-300 after:border after:rounded-full 
                                after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500">
                    </div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-text-primary">Push Notifications</h3>
                    <p className="text-sm text-text-secondary">Receive mobile push notifications</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.push}
                      onChange={(e) => setPreferences({ ...preferences, push: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-border-light peer-focus:outline-none rounded-full peer 
                                peer-checked:after:translate-x-full peer-checked:after:border-white 
                                after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                                after:bg-white after:border-gray-300 after:border after:rounded-full 
                                after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500">
                    </div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-text-primary">Desktop Notifications</h3>
                    <p className="text-sm text-text-secondary">Show browser notifications</p>
                  </div>
                  {notificationsEnabled ? (
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.desktop}
                        onChange={(e) => setPreferences({ ...preferences, desktop: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-border-light peer-focus:outline-none rounded-full peer 
                                  peer-checked:after:translate-x-full peer-checked:after:border-white 
                                  after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                                  after:bg-white after:border-gray-300 after:border after:rounded-full 
                                  after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500">
                      </div>
                    </label>
                  ) : (
                    <button
                      onClick={handleEnableDesktopNotifications}
                      className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                               transition-colors text-sm"
                    >
                      Enable
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Notification Categories */}
            <div className="bg-surface-light rounded-lg p-6">
              <h2 className="text-lg font-semibold text-text-primary mb-4">Notification Categories</h2>
              
              <div className="space-y-4">
                {(Object.keys(preferences.categories) as NotificationType[]).map((category) => (
                  <div key={category} className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-text-primary">{categoryLabels[category]}</h3>
                      <div className="mt-1 space-x-2">
                        {['low', 'medium', 'high'].map((priority) => (
                          <label key={priority} className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={preferences.categories[category].priority.includes(priority)}
                              onChange={(e) => {
                                const newPriorities = e.target.checked
                                  ? [...preferences.categories[category].priority, priority]
                                  : preferences.categories[category].priority.filter(p => p !== priority);
                                setPreferences({
                                  ...preferences,
                                  categories: {
                                    ...preferences.categories,
                                    [category]: {
                                      ...preferences.categories[category],
                                      priority: newPriorities
                                    }
                                  }
                                });
                              }}
                              className="w-4 h-4 text-primary-500 border-border-light rounded 
                                       focus:ring-primary-500"
                            />
                            <span className="ml-2 text-sm text-text-secondary capitalize">{priority}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.categories[category].enabled}
                        onChange={(e) => setPreferences({
                          ...preferences,
                          categories: {
                            ...preferences.categories,
                            [category]: {
                              ...preferences.categories[category],
                              enabled: e.target.checked
                            }
                          }
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-border-light peer-focus:outline-none rounded-full peer 
                                  peer-checked:after:translate-x-full peer-checked:after:border-white 
                                  after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                                  after:bg-white after:border-gray-300 after:border after:rounded-full 
                                  after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500">
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Quiet Hours */}
            <div className="bg-surface-light rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-text-primary">Quiet Hours</h2>
                  <p className="text-sm text-text-secondary">
                    Pause notifications during specific hours
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.quietHours.enabled}
                    onChange={(e) => setPreferences({
                      ...preferences,
                      quietHours: {
                        ...preferences.quietHours,
                        enabled: e.target.checked
                      }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-border-light peer-focus:outline-none rounded-full peer 
                              peer-checked:after:translate-x-full peer-checked:after:border-white 
                              after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                              after:bg-white after:border-gray-300 after:border after:rounded-full 
                              after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500">
                  </div>
                </label>
              </div>

              {preferences.quietHours.enabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={preferences.quietHours.start}
                      onChange={(e) => setPreferences({
                        ...preferences,
                        quietHours: {
                          ...preferences.quietHours,
                          start: e.target.value
                        }
                      })}
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      End Time
                    </label>
                    <input
                      type="time"
                      value={preferences.quietHours.end}
                      onChange={(e) => setPreferences({
                        ...preferences,
                        quietHours: {
                          ...preferences.quietHours,
                          end: e.target.value
                        }
                      })}
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                         transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}